import { useState, useRef, useEffect, useMemo } from 'react';
import { useDebounce } from 'usehooks-ts';
import { ChevronDown, X, Loader2 } from 'lucide-react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { axiosInstance } from '@/Api/axiosV2Config';
import { Tooltip } from '@/Components/ui/Tooltip';

interface AutocompleteSelectProps<T> {
  // Configuration de l'API
  apiEndpoint: string;
  searchParam?: string;

  // Transformation des données
  getOptionLabel: (item: T) => string;
  getOptionValue: (item: T) => string;

  // Valeur et onChange
  value: string | null;
  onChange: (value: string | null, item: T | null) => void;

  // Personnalisation de l'affichage
  label?: string;
  required?: boolean;
  placeholder?: string;
  className?: string;
  renderOption?: (item: T) => React.ReactNode;

  // Options
  pageSize?: number;
  debounceMs?: number;
  minSearchLength?: number;

  // Paramètres API additionnels
  additionalParams?: Record<string, any>;

  // Filtrage client-side
  filterFn?: (item: T) => boolean;
  filterActive?: boolean;

  // État
  disabled?: boolean;

  // Taille
  size?: 'default' | 'sm' | 'xs';

  // Tooltip
  tooltip?: string;

  // Transformation de la réponse API en { nodes, pageInfos }
  transformResponse?: (data: any) => { nodes: T[]; pageInfos?: { hasNextPage: boolean; nextPage?: number } };
}

export function AutocompleteSelect<T>({
  apiEndpoint,
  searchParam = 'search',
  getOptionLabel,
  getOptionValue,
  value,
  onChange,
  label,
  required = false,
  placeholder = 'Sélectionner...',
  className = '',
  renderOption,
  pageSize = 20,
  debounceMs = 300,
  minSearchLength = 0,
  additionalParams = {},
  filterFn,
  filterActive = true,
  size = 'default',
  tooltip,
  transformResponse,
  disabled = false,
}: AutocompleteSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedItem, setSelectedItem] = useState<T | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const debouncedSearch = useDebounce(search, debounceMs);

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery(
    [apiEndpoint, debouncedSearch, additionalParams],
    async ({ pageParam = 0 }) => {
      // `pageParam` est 0-indexé (initial 0, puis valeur de `nextPage` renvoyée
      // par le back). On envoie la taille de page via `limit` uniquement : les
      // endpoints à DTO strict (/societies, /warranties-admin) rejettent tout
      // param hors-whitelist comme `total` (ValidationPipe forbidNonWhitelisted),
      // et les endpoints IListResult (getVendors) acceptent désormais `limit`.
      const params = {
        [searchParam]: debouncedSearch || undefined,
        page: pageParam,
        limit: pageSize,
        ...additionalParams,
      };

      const cleanParams = Object.fromEntries(
        Object.entries(params).filter(([_, v]) => v !== undefined)
      );

      const response = await axiosInstance.get<any>(apiEndpoint, { params: cleanParams });

      const responseData = response.data;

      if (transformResponse) {
        return transformResponse(responseData);
      }

      if (responseData.societies) {
        // /societies est 1-indexé côté API (`page = filters.page || 1`,
        // `skip = (page-1)*limit`). `responseData.page` est donc la page
        // COURANTE (1..totalPages). getNextPageParam doit renvoyer la page
        // SUIVANTE (page+1) — l'ancienne valeur `nextPage: responseData.page`
        // renvoyait la page courante et bouclait à l'infini sur `page=1`.
        // Idem `hasNextPage` : en 1-indexé, il reste des pages tant que
        // `page < totalPages` (l'ancien `< totalPages - 1` ratait la dernière).
        return {
          nodes: responseData.societies,
          pageInfos: {
            hasNextPage: responseData.page < responseData.totalPages,
            hasPreviousPage: responseData.page > 1,
            nextPage: responseData.page + 1,
            previousPage: responseData.page - 1,
            totalCount: responseData.total,
            totalPages: responseData.totalPages,
          },
        };
      }

      if (Array.isArray(responseData)) {
        return {
          nodes: responseData,
          pageInfos: {
            hasNextPage: false,
            hasPreviousPage: false,
            nextPage: 0,
            previousPage: 0,
            totalCount: responseData.length,
            totalPages: 1,
          },
        };
      }

      return responseData;
    },
    {
      getNextPageParam: (lastPage: any) => {
        if (!lastPage?.pageInfos) return undefined;
        return lastPage.pageInfos.hasNextPage ? lastPage.pageInfos.nextPage : undefined;
      },
      enabled: isOpen,
      staleTime: 30000,
    }
  );

  const items = useMemo(() => {
    if (!data?.pages) return [];
    const allNodes = data.pages.flatMap((page) => page.nodes);

    let filtered = allNodes;

    if (filterActive && apiEndpoint.includes('/societies')) {
      filtered = filtered.filter((item: any) => item.isActive !== false);
    }

    if (filterFn) {
      filtered = filtered.filter(filterFn);
    }

    return filtered;
  }, [data, filterFn, filterActive, apiEndpoint]);

  const hasMore = hasNextPage ?? false;

  useEffect(() => {
    if (value && !selectedItem) {
      const fetchSelectedItem = async () => {
        try {
          const params: Record<string, any> = {
            ...additionalParams,
            page: 1,
            limit: 100,
          };

          const cleanParams = Object.fromEntries(
            Object.entries(params).filter(([_, v]) => v !== undefined)
          );

          const response = await axiosInstance.get<any>(apiEndpoint, {
            params: cleanParams,
          });

          const responseData = response.data;
          let nodes: T[] = [];

          if (responseData.societies) {
            nodes = responseData.societies;
          } else if (responseData.nodes) {
            nodes = responseData.nodes;
          }

          if (nodes.length > 0) {
            const item = nodes.find(
              (item) => getOptionValue(item) === value
            );
            if (item) {
              setSelectedItem(item);
            }
          }
        } catch (error) {
          console.error('Error fetching selected item:', error);
        }
      };

      fetchSelectedItem();
    } else if (!value && selectedItem) {
      setSelectedItem(null);
    }
  }, [value, selectedItem, additionalParams, apiEndpoint, getOptionValue]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading && !isFetchingNextPage && isOpen) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore, isLoading, isFetchingNextPage, isOpen, fetchNextPage]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < items.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && items[highlightedIndex]) {
          handleSelect(items[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        break;
    }
  };

  const handleSelect = (item: T) => {
    setSelectedItem(item);
    onChange(getOptionValue(item), item);
    setSearch('');
    setIsOpen(false);
    setHighlightedIndex(- 1);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedItem(null);
    onChange(null, null);
    setSearch('');
    inputRef.current?.focus();
  };

  const inputDisplayValue = useMemo(() => {
    if (!selectedItem) return '';
    return getOptionLabel(selectedItem);
  }, [selectedItem, getOptionLabel]);

  const xs = size === 'xs';
  const sm = size === 'sm' || xs;
  const textClass = sm ? 'tw-text-sm' : 'tw-text-base';
  const paddingClass = label ? (sm ? 'tw-pt-4 tw-pb-1' : 'tw-pt-5 tw-pb-1.5') : (xs ? 'tw-py-1' : sm ? 'tw-py-2' : 'tw-py-2.5');
  const borderRadius = xs ? 'tw-rounded-lg' : 'tw-rounded-xl';
  const labelFloated = sm ? 'tw-top-1 tw-text-[10px]' : 'tw-top-1.5 tw-text-[11px]';
  const labelUnfloated = sm
    ? 'tw-top-[9px] tw-text-sm tw-text-gray-400 peer-focus:tw-top-1 peer-focus:tw-text-[10px]'
    : 'tw-top-[13px] tw-text-base tw-text-gray-400 peer-focus:tw-top-1.5 peer-focus:tw-text-[11px]';
  const hasDisplayValue = !!inputDisplayValue || isOpen;

  return (
    <div className={className}>
      <div ref={containerRef} className="tw-relative">
        <div className="tw-relative">
          <input
            ref={inputRef}
            type="text"
            disabled={disabled}
            className={`tw-w-full tw-pl-4 tw-pr-10 ${paddingClass} ${textClass} tw-border tw-border-gray-200 ${borderRadius} tw-transition-all tw-peer tw-placeholder-transparent focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary/20 focus:tw-border-affinicar-primary ${disabled ? 'tw-bg-gray-100 tw-cursor-not-allowed tw-text-gray-500' : ''}`}
            placeholder={label || placeholder || ' '}
            value={isOpen ? search : inputDisplayValue}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => { if (!disabled) setIsOpen(true); }}
            onKeyDown={handleKeyDown}
          />

          {label && (
            <label
              className={`tw-absolute tw-left-4 tw-pointer-events-none tw-transition-all tw-duration-200 tw-origin-left
                ${hasDisplayValue
                  ? `${labelFloated} tw-text-affinicar-primary`
                  : labelUnfloated
                }
              `}
            >
              {label}
              {!required && <span className="tw-text-gray-400 tw-font-normal tw-ml-1 tw-text-[10px]">(Optionnel)</span>}
              {tooltip && <Tooltip content={tooltip} />}
            </label>
          )}

          <div className="tw-absolute tw-right-2 tw-top-1/2 tw-transform -tw-translate-y-1/2 tw-flex tw-items-center tw-gap-1">
            {value && (
              <button
                type="button"
                onClick={handleClear}
                className="tw-p-1 tw-rounded hover:tw-bg-gray-100"
              >
                <X size={16} className="tw-text-gray-500" />
              </button>
            )}
            <ChevronDown
              size={20}
              className={`tw-text-gray-400 tw-transition-transform ${
                isOpen ? 'tw-rotate-180' : ''
              }`}
            />
          </div>
        </div>

        {isOpen && (
          <div className="tw-absolute tw-z-50 tw-w-full tw-mt-1 tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-lg tw-max-h-60 tw-overflow-auto">
            <ul ref={listRef} className="tw-py-1">
              {items.length === 0 && !isLoading && (
                <li className="tw-px-4 tw-py-2 tw-text-gray-500 tw-text-sm">
                  {debouncedSearch.length < minSearchLength
                    ? `Tapez au moins ${minSearchLength} caractère${minSearchLength > 1 ? 's' : ''} pour rechercher`
                    : 'Aucun résultat trouvé'
                  }
                </li>
              )}

              {items.map((item, index) => {
                const itemValue = getOptionValue(item);
                const isSelected = itemValue === value;
                const isHighlighted = index === highlightedIndex;

                return (
                  <li
                    key={`${itemValue}-${index}`}
                    className={`tw-px-4 tw-py-2 tw-cursor-pointer tw-transition-colors tw-border-b tw-border-gray-100 last:tw-border-0 ${
                      isSelected ? 'tw-bg-affinicar-lighter tw-text-blue-700' : ''
                    } ${
                      isHighlighted && !isSelected ? 'tw-bg-gray-100' : ''
                    } hover:tw-bg-gray-100`}
                    onClick={() => handleSelect(item)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    {renderOption ? renderOption(item) : getOptionLabel(item)}
                  </li>
                );
              })}

              {(isLoading || isFetchingNextPage) && (
                <li className="tw-px-4 tw-py-2 tw-flex tw-items-center tw-justify-center tw-text-gray-500">
                  <Loader2 size={16} className="tw-animate-spin tw-mr-2" />
                  Chargement...
                </li>
              )}

              <div ref={sentinelRef} className="tw-h-1" />
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export default AutocompleteSelect;
